import { useCallback, useEffect, useRef, useState } from "react";
import { useApiFetch } from "../../api/client";
import { useAppUser } from "../../app/AppUserContext";
import { useConfirmDialog } from "../../components/ConfirmDialog";
import { Btn } from "../../components/ui/Button";
import { Chip } from "../../components/ui/Chip";
import { errorMessage } from "../cockpit/format";
import { confidenceLabel, formatLocation, methodLabel, segmentVariant, updatedLabel } from "./format";
import type { FacilityMatchPage, FacilityMatchView, SupplierOption } from "./types";

interface Props {
  onDecided: () => void;
  onOpen: (registryId: string) => void;
  reloadKey: number;
}

const PAGE_SIZE = 50;

export function MatchQueue({ onDecided, onOpen, reloadKey }: Props) {
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const { appRole } = useAppUser();
  const canDecide = appRole === "Uploader" || appRole === "Admin";
  const { confirm, dialog } = useConfirmDialog();

  const [hideUnmatched, setHideUnmatched] = useState(true);
  const [page, setPage] = useState<FacilityMatchPage | null>(null);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [chosen, setChosen] = useState<Record<number, string>>({});
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const query = `facilities/matches?matchState=Pending&hideUnmatched=${hideUnmatched}&limit=${PAGE_SIZE}`;
      const response = await apiFetchRef.current(query);
      setPage(await response.json());
      setLoadedAt(new Date());
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [hideUnmatched]);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  useEffect(() => {
    if (hideUnmatched || suppliers.length > 0) return;
    (async () => {
      try {
        const response = await apiFetchRef.current("suppliers");
        setSuppliers(await response.json());
      } catch (err) {
        setError(errorMessage(err));
      }
    })();
  }, [hideUnmatched, suppliers.length]);

  const decide = async (match: FacilityMatchView, matchState: "Confirmed" | "Rejected") => {
    let notes: string | null = null;
    if (matchState === "Rejected") {
      const reason = await confirm({
        title: "Reject this match?",
        message: `${match.facilityName} will not be attached to ${match.candidateSupplierName ?? "a supplier"}.`,
        reasonLabel: "Reason (optional)",
        confirmLabel: "Reject match",
      });
      if (reason === null) return;
      notes = reason.trim() || null;
    }
    const supplierId = match.candidateSupplierId === null ? Number(chosen[match.matchId]) : null;
    setBusyId(match.matchId);
    try {
      await apiFetchRef.current(`facilities/matches/${match.matchId}`, {
        method: "PATCH",
        body: JSON.stringify({ matchState, notes, ...(supplierId ? { supplierId } : {}) }),
      });
      await load();
      onDecided();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      {dialog}
      <div className="flex items-center gap-4 mb-3 text-[13px] text-rs-light-gray">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={hideUnmatched} onChange={(e) => setHideUnmatched(e.target.checked)} />
          Only facilities with a likely supplier
        </label>
        {page && (
          <span className="ml-auto">
            {page.total.toLocaleString()} waiting{loadedAt ? ` · ${updatedLabel(loadedAt)}` : ""}
          </span>
        )}
      </div>
      {error && <p className="text-rs-red text-[13px]">{error}</p>}
      <div className="card p-0 overflow-auto max-w-full">
        <table>
          <thead>
            <tr>
              <th>Facility</th>
              <th>Proposed supplier</th>
              <th>Why</th>
              {canDecide && <th></th>}
            </tr>
          </thead>
          <tbody>
            {!page && (
              <tr>
                <td colSpan={4}>Loading…</td>
              </tr>
            )}
            {page?.items.length === 0 && (
              <tr>
                <td colSpan={4}>Nothing is waiting for review.</td>
              </tr>
            )}
            {page?.items.map((match) => (
              <tr key={match.matchId}>
                <td>
                  <button
                    className="bg-transparent border-none p-0 text-left text-rs-off-white hover:text-rs-green"
                    onClick={() => onOpen(match.registryId)}
                  >
                    {match.facilityName}
                  </button>
                  <div className="text-xs text-rs-light-gray mt-0.5">
                    {formatLocation(match.city, match.stateCode)} <Chip variant={segmentVariant(match.segment)}>{match.segment}</Chip>
                  </div>
                </td>
                <td>
                  {match.candidateSupplierName ?? (
                    <select
                      className="ctl"
                      value={chosen[match.matchId] ?? ""}
                      disabled={!canDecide}
                      onChange={(e) => setChosen((c) => ({ ...c, [match.matchId]: e.target.value }))}
                    >
                      <option value="">Choose a supplier…</option>
                      {suppliers.map((s) => (
                        <option key={s.supplierId} value={s.supplierId}>
                          {s.supplierName}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
                <td className="text-[13px]">
                  {methodLabel(match.method)}
                  {confidenceLabel(match.confidenceScore) && (
                    <span className="text-rs-light-gray"> · {confidenceLabel(match.confidenceScore)}</span>
                  )}
                  {match.matchedOnValue && <div className="text-xs text-rs-light-gray">{match.matchedOnValue}</div>}
                </td>
                {canDecide && (
                  <td className="whitespace-nowrap">
                    <Btn
                      className="mr-2"
                      disabled={busyId === match.matchId || (match.candidateSupplierId === null && !chosen[match.matchId])}
                      onClick={() => decide(match, "Confirmed")}
                    >
                      Confirm
                    </Btn>
                    <Btn variant="ghost" disabled={busyId === match.matchId} onClick={() => decide(match, "Rejected")}>
                      Reject
                    </Btn>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
